/* * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Saga for Settings Profile component
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @version 1.26.0
 * @since 1.3.0
 */

import { createElement } from 'react';

import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import type { SagaIterator } from 'redux-saga';
import { all, call, debounce, put, select, take, takeEvery } from 'redux-saga/effects';

import { checkResponseErrorWithModal, requestSaga } from '@/main/sagas/requestSaga';
import { setHelpFlagSuccessAction } from '@components/Header/headerActions';
import {
    API_HOSTNAME,
    SETTINGS_PROFILE_META,
    SETTINGS_PROFILE_UPDATE_COMMON,
    SETTINGS_PROFILE_UPDATE_PASSWORD,
    SETTINGS_PROFILE_UPDATE_QUESTION,
} from '@modules/app/Api';
import { DEBOUNCE_DEFAULT_DELAY, EHttpMethod } from '@modules/app/appConstants';
import { makeSelectTranslationPageSettings } from '@modules/internationalization/intSelectors';
import type { IPageSettingsTranslation } from '@modules/internationalization/types/pages/pageSettings';
import ModalPopup from '@modules/modal/components/ModalPopup';
import { modalOpen } from '@modules/modal/modalActions';
import type { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { onboardingInitStartAction, onboardingPageReadyForStartAction } from '@modules/onboarding/onboardingActions';
import { ONBOARDING_INIT_SUCCESS } from '@modules/onboarding/onboardingConstants';
import { EOnboardingPage } from '@modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { setLocalStorageDataAction } from '@modules/storage/storageActions';
import { makeSelectLocalStorageSettingsProfileParams } from '@modules/storage/storageSelectors';
import { ELocalStorageKeys } from '@modules/storage/storageTypes';
import { removeItemLocalStorage } from '@modules/storage/storageUtils';
import { openRoute } from '@routes/constants/externalRoutes';
import { RoutePath } from '@routes/constants/routesConstants';

import { settingsLoaderStartAction, settingsLoaderStopAction } from '../../settingsActions';
import {
    settingsProfileLoadMetaAction,
    settingsProfileLoadMetaFailure,
    settingsProfileLoadMetaSuccess,
    settingsProfileResetParamsAction,
    settingsProfileSaveCommonFailure,
    settingsProfileSaveCommonSuccess,
    settingsProfileSavePasswordFailure,
    settingsProfileSavePasswordSuccess,
    settingsProfileSaveQuestionFailure,
    settingsProfileSaveQuestionSuccess,
    settingsProfileSetIsCommonParamsValidAction,
    settingsProfileSetIsParamsPreferredLKUpdatedAction,
    settingsProfileSetIsParamsUpdatedAction,
    settingsProfileSetParamComponentsAction,
    settingsProfileSetParamContactsAction,
    settingsProfileSetParamPreferredLKAction,
} from './settingsProfileActions';
import {
    ESettingsProfileComponents,
    ESettingsProfilePasswordError,
    ESettingsProfilePasswordFields,
    SETTINGS_PROFILE_INIT_ACTION,
    SETTINGS_PROFILE_LOAD_META_ACTION,
    SETTINGS_PROFILE_LOAD_META_FAILURE,
    SETTINGS_PROFILE_LOAD_META_SUCCESS,
    SETTINGS_PROFILE_RESET_PARAMS_ACTION,
    SETTINGS_PROFILE_SAVE_COMMON_ACTION,
    SETTINGS_PROFILE_SAVE_PASSWORD_ACTION,
    SETTINGS_PROFILE_SAVE_QUESTION_ACTION,
    SETTINGS_PROFILE_SET_PARAM_COMPONENTS_ACTION,
    SETTINGS_PROFILE_SET_PARAM_CONTACTS_ACTION,
    SETTINGS_PROFILE_SET_PARAM_PASSWORD_ACTION,
    SETTINGS_PROFILE_SET_PARAM_PREFERRED_LK_ACTION,
    SETTINGS_PROFILE_SET_PARAM_QUESTION_ACTION,
} from './settingsProfileConstants';
import {
    initialSettingsProfileMetaContacts,
    initialSettingsProfileMetaHints,
    initialSettingsProfileMetaOnboarding,
    initialSettingsProfileMetaPassword,
    initialSettingsProfileMetaQuestion,
} from './settingsProfileReducer';
import {
    makeSelectSettingsProfileIsCommonParamsUpdated,
    makeSelectSettingsProfileIsCommonParamsValid,
    makeSelectSettingsProfileIsPreferredLKParamsUpdated,
    makeSelectSettingsProfileMetaContacts,
    makeSelectSettingsProfileMetaHints,
    makeSelectSettingsProfileMetaOnboarding,
    makeSelectSettingsProfileMetaPreferredLK,
    makeSelectSettingsProfileParamsContacts,
    makeSelectSettingsProfileParamsHints,
    makeSelectSettingsProfileParamsOnboarding,
    makeSelectSettingsProfileParamsPassword,
    makeSelectSettingsProfileParamsPreferredLK,
    makeSelectSettingsProfileParamsQuestion,
} from './settingsProfileSelectors';
import type {
    ISettingsProfileMeta,
    ISettingsProfileMetaContacts,
    ISettingsProfileMetaHints,
    ISettingsProfileMetaResponse,
    ISettingsProfileParams,
    ISettingsProfileParamsContacts,
    ISettingsProfileParamsHints,
    ISettingsProfileParamsOnboarding,
    ISettingsProfileParamsPassword,
    ISettingsProfileParamsQuestion,
    ISettingsProfilePreferredLK,
} from './settingsProfileTypes';

export function* settingsProfileInitSaga(): SagaIterator {
    yield put(settingsLoaderStartAction());

    yield put(settingsProfileLoadMetaAction());
    const loadMetaAction = yield take([SETTINGS_PROFILE_LOAD_META_SUCCESS, SETTINGS_PROFILE_LOAD_META_FAILURE]);
    if (loadMetaAction.type === SETTINGS_PROFILE_LOAD_META_FAILURE) {
        yield put(settingsLoaderStopAction());
        return;
    }

    const storageProfileParams: Omit<
        ISettingsProfileParams,
        ESettingsProfileComponents.PASSWORD | ESettingsProfileComponents.QUESTION
    > = yield select(makeSelectLocalStorageSettingsProfileParams());

    if (storageProfileParams) {
        yield put(
            settingsProfileSetParamContactsAction({
                ...storageProfileParams[ESettingsProfileComponents.CONTACTS],
            }),
        );
        yield put(
            settingsProfileSetParamPreferredLKAction({
                ...storageProfileParams[ESettingsProfileComponents.PREFERRED_LK],
            }),
        );
        yield put(
            settingsProfileSetParamComponentsAction({
                ...storageProfileParams[ESettingsProfileComponents.HINTS],
                component: ESettingsProfileComponents.HINTS,
            }),
        );
        if (yield select(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.onboarding))) {
            yield put(
                settingsProfileSetParamComponentsAction({
                    ...storageProfileParams[ESettingsProfileComponents.ONBOARDING],
                    component: ESettingsProfileComponents.ONBOARDING,
                }),
            );
        }
    } else {
        yield put(
            settingsProfileResetParamsAction({
                params: [
                    ESettingsProfileComponents.CONTACTS,
                    ESettingsProfileComponents.PREFERRED_LK,
                    ESettingsProfileComponents.HINTS,
                    ESettingsProfileComponents.ONBOARDING,
                ],
            }),
        );
    }

    yield put(settingsLoaderStopAction());
    // Onboarding
    yield put(onboardingInitStartAction({ page: EOnboardingPage.settings }));
    yield take(ONBOARDING_INIT_SUCCESS);

    yield put(onboardingPageReadyForStartAction());
}

export function* settingsProfileLoadMetaSaga(): SagaIterator {
    try {
        const response: ISettingsProfileMetaResponse = yield call(
            requestSaga,
            API_HOSTNAME + SETTINGS_PROFILE_META,
            EHttpMethod.GET,
        );
        const {
            email,
            emailIsEnabled,
            phone,
            phoneIsEnabled,
            hintsIsEnabled,
            onboardingIsDisabled,
            recoveryAvailable,
        } = response.metaCommon;

        const metaQuestion = recoveryAvailable ? response.metaQuestion : initialSettingsProfileMetaQuestion;

        const meta: ISettingsProfileMeta = {
            [ESettingsProfileComponents.CONTACTS]: {
                ...initialSettingsProfileMetaContacts,
                email: email ?? '',
                emailIsEnabled,
                phone: phone ?? '',
                phoneIsEnabled,
            },
            [ESettingsProfileComponents.PREFERRED_LK]: {
                preferred: response.preferred,
            },
            [ESettingsProfileComponents.HINTS]: {
                ...initialSettingsProfileMetaHints,
                hintsIsEnabled,
            },
            [ESettingsProfileComponents.ONBOARDING]: {
                ...initialSettingsProfileMetaOnboarding,
                onboardingIsDisabled,
            },
            [ESettingsProfileComponents.PASSWORD]: {
                ...initialSettingsProfileMetaPassword,
                ...response.metaPassword,
            },
            [ESettingsProfileComponents.QUESTION]: { ...metaQuestion },
        };
        yield put(
            settingsProfileLoadMetaSuccess({
                meta,
                isRecoveryAvailable: recoveryAvailable,
            }),
        );
    } catch (e: any) {
        yield put(settingsProfileLoadMetaFailure({ message: e }));
    }
}

export function* settingsProfileStorageSaveSaga(): SagaIterator {
    const paramsContacts: ISettingsProfileParamsContacts = yield select(makeSelectSettingsProfileParamsContacts());
    const paramsPreferredLK: ISettingsProfilePreferredLK = yield select(makeSelectSettingsProfileParamsPreferredLK());
    const paramsHints: ISettingsProfileParamsHints = yield select(makeSelectSettingsProfileParamsHints());
    const paramsOnboarding: ISettingsProfileParamsOnboarding = yield select(
        makeSelectSettingsProfileParamsOnboarding(),
    );
    yield put(
        setLocalStorageDataAction({
            key: ELocalStorageKeys.SETTINGS_PROFILE_PARAMS,
            value: {
                [ESettingsProfileComponents.CONTACTS]: paramsContacts,
                [ESettingsProfileComponents.PREFERRED_LK]: paramsPreferredLK,
                [ESettingsProfileComponents.HINTS]: paramsHints,
                [ESettingsProfileComponents.ONBOARDING]: paramsOnboarding,
            },
        }),
    );
}

export function* settingsProfileStorageRemoveSaga() {
    yield call(removeItemLocalStorage, ELocalStorageKeys.SETTINGS_PROFILE_PARAMS);
}

export function* settingsProfileCheckCommonParamsUpdatedSaga(): SagaIterator {
    const paramsContacts: ISettingsProfileParamsContacts = yield select(makeSelectSettingsProfileParamsContacts());
    const checkParamsContacts: Omit<ISettingsProfileParamsContacts, 'emailErrorCode' | 'phoneErrorCode'> = omit(
        paramsContacts,
        ['emailErrorCode', 'phoneErrorCode'],
    );
    const paramsHints: ISettingsProfileParamsHints = yield select(makeSelectSettingsProfileParamsHints());
    const paramsOnboarding: ISettingsProfileParamsOnboarding = yield select(
        makeSelectSettingsProfileParamsOnboarding(),
    );

    const metaContacts: ISettingsProfileMetaContacts = yield select(makeSelectSettingsProfileMetaContacts());
    const metaHints: ISettingsProfileMetaHints = yield select(makeSelectSettingsProfileMetaHints());
    const metaOnboarding: ISettingsProfileParamsOnboarding = yield select(makeSelectSettingsProfileMetaOnboarding());

    let isParamsUpdated = false;
    if (
        !isEqual(
            {
                ...checkParamsContacts,
                ...paramsHints,
                onboardingIsDisabled: paramsOnboarding.onboardingIsDisabled || false,
            },
            { ...metaContacts, ...metaHints, onboardingIsDisabled: metaOnboarding.onboardingIsDisabled || false },
        )
    ) {
        isParamsUpdated = true;
    }

    const stateParamsUpdated: boolean = yield select(makeSelectSettingsProfileIsCommonParamsUpdated());
    if (stateParamsUpdated !== isParamsUpdated)
        yield put(
            settingsProfileSetIsParamsUpdatedAction({
                isCommonParamsUpdated: isParamsUpdated,
            }),
        );
}

export function* settingsProfileCheckPreferredLKParamsUpdatedSaga(): SagaIterator {
    const paramsPreferredLK: ISettingsProfilePreferredLK = yield select(makeSelectSettingsProfileParamsPreferredLK());
    const metaPreferredLK: ISettingsProfilePreferredLK = yield select(makeSelectSettingsProfileMetaPreferredLK());

    const isParamsUpdated = paramsPreferredLK.preferred !== metaPreferredLK.preferred;

    const statePreferredLKParamsUpdated: boolean = yield select(makeSelectSettingsProfileIsPreferredLKParamsUpdated());
    if (statePreferredLKParamsUpdated !== isParamsUpdated) {
        yield put(
            settingsProfileSetIsParamsPreferredLKUpdatedAction({
                isPreferredLKParamsUpdated: isParamsUpdated,
            }),
        );
    }
}

export function* settingsProfileValidateCommonParamsSaga(): SagaIterator {
    const stateParamsUpdated: boolean = yield select(makeSelectSettingsProfileIsCommonParamsValid());
    const paramsQuestion: ISettingsProfileParamsQuestion = yield select(makeSelectSettingsProfileParamsQuestion());
    const paramContacts: ISettingsProfileParamsContacts = yield select(makeSelectSettingsProfileParamsContacts());
    const paramPassword: ISettingsProfileParamsPassword = yield select(makeSelectSettingsProfileParamsPassword());

    const errorsQuestion = pick(paramsQuestion, ['answerErrorCode']);
    const errorsContacts = pick(paramContacts, ['emailErrorCode', 'phoneErrorCode']);
    const errorsPassword = pick(paramPassword, [
        ESettingsProfilePasswordError.CURRENT,
        ESettingsProfilePasswordError.NEW,
        ESettingsProfilePasswordError.REPEAT,
    ]);

    let isParamsValid = true;

    Object.values({
        ...errorsQuestion,
        ...errorsContacts,
        ...errorsPassword,
    }).forEach((value) => (value ? (isParamsValid = false) : null));

    if (stateParamsUpdated !== isParamsValid)
        yield put(
            settingsProfileSetIsCommonParamsValidAction({
                isCommonParamsValid: isParamsValid,
            }),
        );
}

export function* settingsProfileSaveCommonSaga(): SagaIterator {
    const paramsContacts: ISettingsProfileParamsContacts = yield select(makeSelectSettingsProfileParamsContacts());
    const paramsPreferredLK: ISettingsProfilePreferredLK = yield select(makeSelectSettingsProfileParamsPreferredLK());
    const paramsHints: ISettingsProfileParamsHints = yield select(makeSelectSettingsProfileParamsHints());
    const paramsOnboarding: ISettingsProfileParamsOnboarding = yield select(
        makeSelectSettingsProfileParamsOnboarding(),
    );
    const metaOnboarding: ISettingsProfileParamsOnboarding = yield select(makeSelectSettingsProfileMetaOnboarding());
    const settingsTranslation: IPageSettingsTranslation = yield select(makeSelectTranslationPageSettings());

    try {
        yield put(settingsLoaderStartAction());
        const paramsContactsToSave = omit(paramsContacts, ['emailErrorCode', 'phoneErrorCode']);

        const isOnboardingFeature = yield select(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.onboarding));

        const payload = {
            ...paramsContactsToSave,
            ...paramsHints,
            ...(isOnboardingFeature ? paramsOnboarding : {}),
            ...{ preferredLk: paramsPreferredLK.preferred },
        };

        const url: string = API_HOSTNAME + SETTINGS_PROFILE_UPDATE_COMMON;
        yield call(requestSaga, url, EHttpMethod.POST, payload);
        yield put(settingsProfileSaveCommonSuccess());
        yield put(setHelpFlagSuccessAction(payload.hintsIsEnabled));

        yield put(
            modalOpen({
                render: (props: IModalRenderProps) =>
                    createElement(ModalPopup, {
                        dataName: 'modal-popup-settings-profile-common-success',
                        message: settingsTranslation.profile.submitMessage.common,
                        onCloseModal: props.onCloseModal,
                    }),
            }),
        );
    } catch (e: any) {
        yield put(settingsProfileSaveCommonFailure({ message: e }));
        yield call(checkResponseErrorWithModal, e);
    } finally {
        yield put(settingsLoaderStopAction());
    }

    if (paramsOnboarding.onboardingIsDisabled !== metaOnboarding.onboardingIsDisabled) {
        // Onboarding
        yield put(onboardingInitStartAction({ page: EOnboardingPage.settings }));
    }
}

export function* settingsProfileSaveQuestionSaga(): SagaIterator {
    try {
        const paramsQuestion: ISettingsProfileParamsQuestion = yield select(makeSelectSettingsProfileParamsQuestion());
        const settingsTranslation: IPageSettingsTranslation = yield select(makeSelectTranslationPageSettings());
        const paramsQuestionToSave = {
            answer: paramsQuestion.answer,
            currentQuestionId: paramsQuestion.selectedQuestion.questionId,
        };
        const url: string = API_HOSTNAME + SETTINGS_PROFILE_UPDATE_QUESTION;
        yield call(requestSaga, url, EHttpMethod.POST, paramsQuestionToSave);
        yield put(
            settingsProfileSaveQuestionSuccess({
                selectedQuestion: paramsQuestion.selectedQuestion.questionText,
            }),
        );
        yield put(
            modalOpen({
                render: (props: IModalRenderProps) =>
                    createElement(ModalPopup, {
                        dataName: 'modal-popup-settings-profile-question-success',
                        message: settingsTranslation.profile.submitMessage.question,
                        onCloseModal: props.onCloseModal,
                    }),
            }),
        );
    } catch (e: any) {
        yield put(settingsProfileSaveQuestionFailure({ message: e }));
        yield call(checkResponseErrorWithModal, e);
    }
}

export function* settingsProfileSavePasswordSaga(): SagaIterator {
    try {
        const paramsPassword: ISettingsProfileParamsPassword = yield select(makeSelectSettingsProfileParamsPassword());
        const settingsTranslation: IPageSettingsTranslation = yield select(makeSelectTranslationPageSettings());
        const paramsPasswordToSave = pick(paramsPassword, [
            ESettingsProfilePasswordFields.CURRENT,
            ESettingsProfilePasswordFields.NEW,
        ]);

        const url: string = API_HOSTNAME + SETTINGS_PROFILE_UPDATE_PASSWORD;
        yield call(requestSaga, url, EHttpMethod.POST, paramsPasswordToSave);
        yield put(settingsProfileSavePasswordSuccess());
        yield put(
            modalOpen({
                onCloseCallback: () => openRoute(RoutePath.logout),
                render: (props: IModalRenderProps) =>
                    createElement(ModalPopup, {
                        dataName: 'modal-popup-settings-profile-password-success',
                        message: settingsTranslation.profile.submitMessage.password,
                        onCloseModal: props.onCloseModal,
                    }),
            }),
        );
    } catch (e: any) {
        yield put(settingsProfileSavePasswordFailure({ message: e }));
        yield call(checkResponseErrorWithModal, e);
    }
}

export function* settingsProfileSaga(): SagaIterator {
    yield all([
        debounce(
            DEBOUNCE_DEFAULT_DELAY,
            [
                SETTINGS_PROFILE_SET_PARAM_CONTACTS_ACTION,
                SETTINGS_PROFILE_SET_PARAM_PREFERRED_LK_ACTION,
                SETTINGS_PROFILE_SET_PARAM_COMPONENTS_ACTION,
            ],
            settingsProfileStorageSaveSaga,
        ),
        takeEvery(SETTINGS_PROFILE_RESET_PARAMS_ACTION, settingsProfileStorageRemoveSaga),
        takeEvery(
            [
                SETTINGS_PROFILE_SET_PARAM_CONTACTS_ACTION,
                SETTINGS_PROFILE_SET_PARAM_PREFERRED_LK_ACTION,
                SETTINGS_PROFILE_SET_PARAM_COMPONENTS_ACTION,
                SETTINGS_PROFILE_SET_PARAM_PASSWORD_ACTION,
                SETTINGS_PROFILE_SET_PARAM_QUESTION_ACTION,
                SETTINGS_PROFILE_RESET_PARAMS_ACTION,
            ],
            settingsProfileCheckCommonParamsUpdatedSaga,
        ),
        takeEvery(
            [
                SETTINGS_PROFILE_SET_PARAM_CONTACTS_ACTION,
                SETTINGS_PROFILE_SET_PARAM_PREFERRED_LK_ACTION,
                SETTINGS_PROFILE_RESET_PARAMS_ACTION,
            ],
            settingsProfileCheckPreferredLKParamsUpdatedSaga,
        ),
        takeEvery(
            [
                SETTINGS_PROFILE_SET_PARAM_CONTACTS_ACTION,
                SETTINGS_PROFILE_SET_PARAM_PREFERRED_LK_ACTION,
                SETTINGS_PROFILE_SET_PARAM_COMPONENTS_ACTION,
                SETTINGS_PROFILE_SET_PARAM_PASSWORD_ACTION,
                SETTINGS_PROFILE_SET_PARAM_QUESTION_ACTION,
                SETTINGS_PROFILE_RESET_PARAMS_ACTION,
            ],
            settingsProfileValidateCommonParamsSaga,
        ),
        takeEvery(SETTINGS_PROFILE_INIT_ACTION, settingsProfileInitSaga),
        takeEvery(SETTINGS_PROFILE_LOAD_META_ACTION, settingsProfileLoadMetaSaga),
        takeEvery(SETTINGS_PROFILE_SAVE_COMMON_ACTION, settingsProfileSaveCommonSaga),
        takeEvery(SETTINGS_PROFILE_SAVE_PASSWORD_ACTION, settingsProfileSavePasswordSaga),
        takeEvery(SETTINGS_PROFILE_SAVE_QUESTION_ACTION, settingsProfileSaveQuestionSaga),
    ]);
}
